import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './fonts/cover-fonts.css'; // COVER-001: self-hosted Lora so the cover preview matches the PDF exactly
import App from './App';
import { Amplify } from 'aws-amplify';
import amplifyconfig from './amplifyconfiguration.json';
import { installClientErrorReporting } from './utils/clientErrorReport'; // F-118: client error telemetry
import { callAi } from './utils/aiClient';

// App version — bump this with each deploy to bust caches
const APP_VERSION = '2.4.5';

// Force refresh if user has stale cached version
const storedVersion = localStorage.getItem('remnant_version');
if (storedVersion && storedVersion !== APP_VERSION) {
  localStorage.setItem('remnant_version', APP_VERSION);
  window.location.reload(true);
} else {
  localStorage.setItem('remnant_version', APP_VERSION);
}

// Kill any stale service workers
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(r => r.unregister());
  });
}

// Canonical domain: redirect www → non-www
if (window.location.hostname === 'www.leavearemnant.com') {
  window.location.replace('https://leavearemnant.com' + window.location.pathname + window.location.search);
} else {
  // Configure Amplify unconditionally, before any route branching, so the app can
  // NEVER run un-configured regardless of which page loaded first. Public pages
  // (/gift, /contribute, /s) don't call Amplify, and configure() makes no network
  // calls, so this is inert for them. Previously configure() only ran on the main-app
  // branch, which left users who entered via a public page (e.g. an ad → /gift → app)
  // stranded with "Amplify has not been configured" + auth failures (chapters appear
  // missing until a hard refresh re-runs this file).
  Amplify.configure(amplifyconfig);

  // F-118: client-side error telemetry — window.onerror + unhandledrejection -> the EXISTING /feedback path
  // (message/stack/bundle/route only, NO customer content; deduped + rate-limited). Ends the "we can't see
  // what the device did" blind spot that blinded the F-114/F-115 forensics. Unauth POSTs 401 and are swallowed.
  installClientErrorReporting({
    send: (payload) => callAi(payload, { path: '/feedback' }).catch(() => {}),
    appVersion: APP_VERSION,
  });

  // Skip Amplify-backed rendering for public pages — they need no auth
  const contributeMatch = window.location.pathname.match(/^\/contribute\/([a-zA-Z0-9_-]+)\/?$/);
  const shareMatch = window.location.pathname.match(/^\/s\/([a-zA-Z0-9_-]+)\/?$/);
  const giftMatch = window.location.pathname.match(/^\/gift(\/(success|print))?\/?$/);
  const celebrationMatch = window.location.pathname.match(/^\/celebration\/?$/);
  const confirmMatch = window.location.pathname.match(/^\/confirm\/?$/);

  if (confirmMatch) {
    // One-tap email confirmation landing (from the signup email link). Amplify is
    // already configured above, so ConfirmPage can call confirmSignUp.
    const ConfirmPage = require('./ConfirmPage').default;
    const params = new URLSearchParams(window.location.search);
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <ConfirmPage token={params.get('t')} />
      </React.StrictMode>
    );
  } else if (celebrationMatch) {
    // Render CelebrationPage directly without Amplify — public marketing landing page
    const CelebrationPage = require('./CelebrationPage').default;
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <CelebrationPage />
      </React.StrictMode>
    );
  } else if (giftMatch) {
    // Render GiftPage directly without Amplify — public guest gift checkout (TB-087)
    const GiftPage = require('./GiftPage').default;
    const giftView = giftMatch[2] === 'success' ? 'success'
      : giftMatch[2] === 'print' ? 'print'
      : 'buy';
    const params = new URLSearchParams(window.location.search);
    const giftCode = params.get('code');
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <GiftPage view={giftView} code={giftCode} />
      </React.StrictMode>
    );
  } else if (contributeMatch) {
    // Render ContributePage directly without Amplify
    const ContributePage = require('./ContributePage').default;
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <ContributePage token={contributeMatch[1]} />
      </React.StrictMode>
    );
  } else if (shareMatch) {
    // Render PlaybackPage directly without Amplify — public media playback
    const PlaybackPage = require('./PlaybackPage').default;
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <PlaybackPage shareId={shareMatch[1]} />
      </React.StrictMode>
    );
  } else {
    // Amplify already configured above.
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}